import global from "../../../common/utility/global";
import { NOMBRES_MES_CORTO } from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda/tooltip mantengan siempre el
// mismo ancho/tamano; sin esto, un producto con nombre muy largo estira la
// leyenda y deforma el layout del grafico.
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "2026-08-27T00:00:00" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

function construirKpi(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

// Kpi sin periodo anterior disponible: sin tendencia (la tarjeta la oculta).
function construirKpiSinTendencia(valor) {
    return { valor, tendenciaPct: null, tendenciaDireccion: "flat" };
}

// Agrupa el detalle diario por producto para una moneda ("pen" | "usd") y
// devuelve los productos ordenados de mayor a menor venta en ESA moneda.
function acumularProductos(detalle, moneda) {
    const campo = moneda === "pen" ? "totalSoles" : "totalDolares";
    const acumulado = {};
    detalle.forEach((fila) => {
        acumulado[fila.nombre] = (acumulado[fila.nombre] || 0) + (Number(fila[campo]) || 0);
    });
    return Object.keys(acumulado)
        .map((nombre) => ({ nombre, valor: acumulado[nombre] }))
        .sort((a, b) => b.valor - a.valor);
}

// Serie de "Evolucion" (top 4 productos de la moneda): agrupa por dia si el
// filtro cae en un mismo mes, o por mes si abarca mas de uno.
function construirEvolucion(detalleOrdenado, ordenClaves, esMismoMes, ordenados, moneda) {
    const campo = moneda === "pen" ? "totalSoles" : "totalDolares";
    const nombresTop = ordenados.slice(0, 4).map((item) => item.nombre);
    const acumulado = {};
    nombresTop.forEach((nombre) => {
        acumulado[nombre] = {};
    });
    detalleOrdenado.forEach((fila) => {
        if (!acumulado[fila.nombre]) return;
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        acumulado[fila.nombre][clave] = (acumulado[fila.nombre][clave] || 0) + (Number(fila[campo]) || 0);
    });
    return nombresTop.map((nombre) => ({
        name: nombre,
        data: ordenClaves.map((clave) => Math.round((acumulado[nombre][clave] || 0) * 100) / 100),
    }));
}

function construirParticipacion(ordenados) {
    const total = ordenados.reduce((suma, item) => suma + item.valor, 0);
    const principales = ordenados.slice(0, 4);
    const otros = ordenados.slice(4);
    const base = otros.length > 0
        ? [...principales, { nombre: `Otros (${otros.length} productos)`, valor: otros.reduce((suma, item) => suma + item.valor, 0) }]
        : principales;
    return base.map((item) => ({
        nombre: item.nombre,
        total: item.valor,
        porcentaje: total > 0 ? (item.valor / total) * 100 : 0,
    }));
}

function construirConcentracionTop10(ordenados) {
    const total = ordenados.reduce((suma, item) => suma + item.valor, 0);
    const top10 = ordenados.slice(0, 10).reduce((suma, item) => suma + item.valor, 0);
    return total > 0 ? (top10 / total) * 100 : 0;
}

// Arma la data del dashboard a partir de la respuesta real de
// comprobanteService.dashboardProducto:
// - productos: detalle diario (nombre, fecha, totalSoles, totalDolares), una
//   fila por producto y fecha con ventas.
// - kpis: arreglo de un solo elemento con los totales del periodo y su
//   comparacion contra el periodo anterior (sufijo "Anterior").
// Sin respuesta o sin ventas en el periodo, todo queda en cero/vacio.
export function buildDashboardData(response, filter) {
    const detalle = (response && response.productos) || [];
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};
    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;

    const detalleOrdenado = detalle
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha);

    const ordenClaves = [];
    const vistos = new Set();
    detalleOrdenado.forEach((fila) => {
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        if (!vistos.has(clave)) {
            vistos.add(clave);
            ordenClaves.push(clave);
        }
    });

    const ordenadosPen = acumularProductos(detalle, "pen");
    const ordenadosUsd = acumularProductos(detalle, "usd");

    // El backend puede devolver nombresActivos en 0 aunque haya detalle; en
    // ese caso se cuenta directamente los productos distintos del detalle.
    const productosVendidos = Number(kpisApi.nombresActivos) || ordenadosPen.length;

    return {
        kpis: {
            totalVentas: {
                pen: construirKpi(kpisApi.totalVentasSoles, kpisApi.totalVentasSolesAnterior),
                usd: construirKpi(kpisApi.totalVentasDolares, kpisApi.totalVentasDolaresAnterior),
            },
            // No dependen de la moneda: son conteos.
            productosVendidos: construirKpi(productosVendidos, kpisApi.nombresActivosAnterior),
            cantidadCpe: construirKpi(kpisApi.cantidadCPE, kpisApi.cantidadCPEAnterior),
            cpePromedio: {
                pen: construirKpi(kpisApi.cpePromedioSoles, kpisApi.cpePromedioSolesAnterior),
                usd: construirKpi(kpisApi.cpePromedioDolares, kpisApi.cpePromedioDolaresAnterior),
            },
            // El backend no expone el periodo anterior de esta metrica.
            concentracionTop10: {
                pen: construirKpiSinTendencia(construirConcentracionTop10(ordenadosPen)),
                usd: construirKpiSinTendencia(construirConcentracionTop10(ordenadosUsd)),
            },
        },
        topProductos: {
            pen: ordenadosPen.slice(0, 10),
            usd: ordenadosUsd.slice(0, 10),
        },
        participacion: {
            pen: construirParticipacion(ordenadosPen),
            usd: construirParticipacion(ordenadosUsd),
        },
        evolucion: {
            timeLine: ordenClaves,
            pen: construirEvolucion(detalleOrdenado, ordenClaves, esMismoMes, ordenadosPen, "pen"),
            usd: construirEvolucion(detalleOrdenado, ordenClaves, esMismoMes, ordenadosUsd, "usd"),
        },
    };
}
